import { API_ENDPOINTS } from "../../enums/apiUrls";
import { logError } from "../../utils/customLogger";
import { getBearerToken } from "./bearerToken";

/** Document type the categorization layer assigned to a fax letter for one J-code (backend `FaxLetterCategory`). */
export enum FaxLetterCategory {
  Approval = "approval",
  NoAuthRequired = "no_auth_required",
  Denial = "denial",
  AdditionalInfoP2P = "additional_info_p2p",
  UnableToProcess = "unable_to_process",
  Renewal = "renewal",
  Others = "others",
  Unclassified = "unclassified",
}

/** Stable denial-reason codes used by AI extraction and the backend review contract. */
export enum FaxDenialReason {
  StepTherapy = "step_therapy",
  Indication = "indication_not_supported",
  ClinicalCriteria = "clinical_criteria_not_met",
  Continuation = "continuation_criteria_not_met",
  PolicyLimit = "policy_limit_exceeded",
  Documentation = "insufficient_documentation",
  Coverage = "coverage_exclusion",
  Provider = "provider_site_network",
  Other = "other",
}

/** Whether a reviewer has saved a decision on the letter (backend `FaxLetterReviewStatus`). */
export enum FaxLetterReviewStatus {
  PendingReview = "pending_review",
  Reviewed = "reviewed",
}

/** The reviewer's verdict (backend `FaxLetterDecision`). */
export enum FaxLetterDecision {
  Approved = "approved",
  Rejected = "rejected",
}

/** Identifies one attached letter on one J-code. */
export interface FaxLetterRequest {
  order_id: string;
  org_id: string;
  j_code_id: string;
  document_id: string;
}

export interface OrderFaxLetter {
  j_code_id: string;
  document_id: string;
  category: FaxLetterCategory;
  review_status: FaxLetterReviewStatus;
  attached_at?: string | null;
}

export interface FaxLetterDocument {
  id?: string | null;
  doc_type?: string | null;
  category?: FaxLetterCategory | null;
  /** Attached letter ID for opening its human review page, including an uploaded copy. */
  review_document_id?: string | null;
  doc_title?: string | null;
  doc_path?: string | null;
  doc_upload_date?: string | null;
}

export interface FaxLetterIdentification {
  patient_name?: string | null;
  member_id?: string | null;
  payer?: string | null;
  j_code?: string | null;
  medicine_name?: string | null;
  date_of_service?: string | null;
}

export interface FaxLetterAuthorization {
  category: FaxLetterCategory;
  reference_number?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  num_visits?: string | null;
  quantity?: string | null;
  denial_reason?: string | null;
  denial_reason_category?: FaxDenialReason | null;
  notes?: string | null;
}

/** An Authorization field a reviewer may correct. */
export type CorrectableField = keyof FaxLetterAuthorization;

export interface FaxLetterReview {
  decision: FaxLetterDecision;
  corrections: Partial<Record<CorrectableField, string | null>>;
  additional_comments?: string | null;
  document_name?: string | null;
  reviewed_by?: string | null;
  reviewed_at?: string | null;
  uploaded_document_id?: string | null;
}

export interface FaxLetterDetailsResponse {
  order_id: string;
  j_code_id: string;
  document: FaxLetterDocument;
  identification: FaxLetterIdentification;
  /** The letter as read by the categorization layer (corrections live on `review`). */
  authorization: FaxLetterAuthorization;
  validation_errors?: Partial<Record<CorrectableField, string>>;
  review_status: FaxLetterReviewStatus;
  review?: FaxLetterReview | null;
  received_date?: string | null;
  previous_documents: FaxLetterDocument[];
}

export interface FaxLetterReviewRequest extends FaxLetterRequest {
  decision: FaxLetterDecision;
  corrections: Partial<Record<CorrectableField, string | null>>;
  additional_comments?: string | null;
  document_name: string;
  /** "Save and Upload" (true) or "No, Just Save" (false). */
  upload: boolean;
}

export interface FaxLetterReviewResponse {
  review_status: FaxLetterReviewStatus;
  uploaded_document_id?: string | null;
}

export interface FaxLetterMarkIncorrectResponse {
  /** True when no J-code holds the letter any more (it joined the unmatched pile). */
  fax_unmatched: boolean;
}

/** A failed fax-letter request; `status` is the HTTP status (0 when no response was received). */
export class FaxLetterApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "FaxLetterApiError";
  }
}

/**
 * POSTs `body` to a fax-letter endpoint with the caller's bearer token.
 *
 * @param url - Endpoint URL
 * @param body - JSON request body
 * @param label - Operation name for error logs
 * @returns The parsed JSON response
 * @throws FaxLetterApiError - on a missing token, a non-2xx response, or a network failure
 */
async function postFaxLetter<T>(
  url: string,
  body: object,
  label: string,
): Promise<T> {
  const token = await getBearerToken();
  if (!token) {
    throw new FaxLetterApiError("No bearer token", 0);
  }
  let response: Response;
  try {
    response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
  } catch (e) {
    logError(e instanceof Error ? e : new Error(String(e)));
    throw new FaxLetterApiError("Network error", 0);
  }
  if (!response.ok) {
    logError(
      new Error(`${label} failed: ${response.status} ${response.statusText}`),
    );
    throw new FaxLetterApiError(
      `Request failed with status ${response.status}`,
      response.status,
    );
  }
  return (await response.json()) as T;
}

/**
 * Lists the fax letters attached to an order's J-codes (for the Drugs tab's document icon).
 *
 * @param body - Order and org
 * @returns The attached letters, newest first
 */
export const getOrderFaxLetters = async (body: {
  order_id: string;
  org_id: string;
}): Promise<OrderFaxLetter[]> =>
  (
    await postFaxLetter<{ fax_letters: OrderFaxLetter[] }>(
      API_ENDPOINTS.FAX_LETTER_ORDER,
      body,
      "getOrderFaxLetters",
    )
  ).fax_letters;

/**
 * Fetches the Authorization Details page data for one attached letter.
 *
 * @param body - The letter's order, org, J-code row id, and attachment id
 * @returns Identification, Authorization as read, review state, and previous documents
 */
export const getFaxLetterDetails = (
  body: FaxLetterRequest,
): Promise<FaxLetterDetailsResponse> =>
  postFaxLetter(API_ENDPOINTS.FAX_LETTER_DETAILS, body, "getFaxLetterDetails");

/**
 * Saves a reviewer's decision; with `upload`, also adds the letter to the J-code's documents.
 *
 * @param body - The letter, decision, corrections, document name, and `upload`
 * @returns The new review status and the uploaded document id, if uploaded
 */
export const saveFaxLetterReview = (
  body: FaxLetterReviewRequest,
): Promise<FaxLetterReviewResponse> =>
  postFaxLetter(API_ENDPOINTS.FAX_LETTER_REVIEW, body, "saveFaxLetterReview");

/**
 * Takes the letter off the J-code ("Incorrect Letter").
 *
 * @param body - The letter's order, org, J-code row id, and attachment id
 * @returns Whether the fax went back to the unmatched pile
 */
export const markFaxLetterIncorrect = (
  body: FaxLetterRequest,
): Promise<FaxLetterMarkIncorrectResponse> =>
  postFaxLetter(
    API_ENDPOINTS.FAX_LETTER_MARK_INCORRECT,
    body,
    "markFaxLetterIncorrect",
  );
